import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiConsumer } from "../../service/api-consumer";

/**
 *
 * This Redux slice is responsible for managing the state related to time zones
 *
 * Functionality:
 *  - Fetches a list of available time zones from the server (GET DropDownList/GetTimezones).
 *  - Manages loading and error states for both fetch and send operations.
 *
 * State Structure:
 *  - timeZoneData: string[] — List of all available time zones fetched from the server.
 *  - loading: boolean — Indicates whether the time zones are being fetched.
 *  - error: unknown | null — Stores any error that occurred during fetch.
 *  - sending: boolean — Indicates whether the selected time zone is being sent to the server.
 *  - sendError: string | null — Stores any error that occurred during the send operation.
 *
 * Async Thunks:
 *  - fetchTimeZone(): Fetches time zone list from the API.
 *
 * Extra Reducers:
 *  - Handles the pending, fulfilled, and rejected states of fetchTimeZone and setTimeZone.
 *
 * Usage:
 *  - Import this slice's reducer and add it to your Redux store.
 *  - Use dispatch(fetchTimeZone()) to load time zones.
 */

export const fetchTimeZone = createAsyncThunk(
  "data/fetchTimeZone",
  async () => {
    const response = await ApiConsumer.getTimezones();
    return response;
  }
);

interface TimeZoneState {
  timezoneData: string[];
  loading: boolean;
  error: unknown | null;
  sending: boolean;
  sendError: string | null;
}

const initialState: TimeZoneState = {
  timezoneData: [],
  loading: false,
  error: null,
  sending: false,
  sendError: null,
};

const timeZoneSlice = createSlice({
  name: "timeZoneData",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTimeZone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimeZone.fulfilled, (state, action) => {
        state.loading = false;
        state.timezoneData = action.payload;
      })
      .addCase(fetchTimeZone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
  },
});

export default timeZoneSlice.reducer;
